import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy, ExternalLink, History, Link2, Loader2, QrCode, Share2, Users } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { supabase } from '../supabaseClient';

type RosteredEvent = {
  id: string;
  title?: string;
  eventDate?: string;
  venue?: string;
};

type ReferralLink = {
  link_id: string;
  link_scope: 'PERMANENT' | 'EVENT_SPECIFIC';
  event_shift_id: string | null;
  token: string;
  label: string | null;
  is_active: boolean;
  expires_at: string | null;
  revoked_at: string | null;
  created_at: string;
};

type ReferralHistoryItem = {
  referral_id: string;
  referred_at: string;
  event_type: string | null;
  event_date: string | null;
  lead_status: string | null;
  client_benefit_status: string | null;
  reward_status: string | null;
};

type Props = {
  rosteredEvents: RosteredEvent[];
};

const WEBSITE_BASE_URL = (
  process.env.REACT_APP_QUIKPIX_WEBSITE_URL
  || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3002')
).replace(/\/$/, '');

const formatStatus = (value: string | null, fallback: string) => {
  if (!value) return fallback;
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const linkIsUsable = (link: ReferralLink) => {
  if (!link.is_active || link.revoked_at) return false;
  return !link.expires_at || new Date(link.expires_at).getTime() > Date.now();
};

export const CrewReferralPanel: React.FC<Props> = ({ rosteredEvents }) => {
  const [links, setLinks] = useState<ReferralLink[]>([]);
  const [history, setHistory] = useState<ReferralHistoryItem[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');

  const safeRosteredEvents = useMemo(
    () => rosteredEvents.filter((event, index, all) => (
      Boolean(event.id) && all.findIndex((candidate) => candidate.id === event.id) === index
    )),
    [rosteredEvents],
  );

  const loadReferralData = useCallback(async () => {
    setLoading(true);
    setError('');
    const [linksResult, historyResult] = await Promise.all([
      supabase.rpc('get_my_referral_links'),
      supabase.rpc('get_my_referral_history'),
    ]);

    if (linksResult.error || historyResult.error) {
      setError('Referral details are unavailable right now. Please try again.');
      setLoading(false);
      return;
    }

    setLinks((linksResult.data || []) as ReferralLink[]);
    setHistory((historyResult.data || []) as ReferralHistoryItem[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadReferralData();
  }, [loadReferralData]);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(''), 2500);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const permanentLink = links.find((link) => link.link_scope === 'PERMANENT' && linkIsUsable(link));
  const selectedEventLink = links.find((link) => (
    link.link_scope === 'EVENT_SPECIFIC'
    && link.event_shift_id === selectedEventId
    && linkIsUsable(link)
  ));
  const activeLink = selectedEventId ? selectedEventLink : permanentLink;
  const referralUrl = activeLink ? `${WEBSITE_BASE_URL}/r/${activeLink.token}` : '';

  const copyLink = async (message = 'Link copied') => {
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setFeedback(message);
    } catch {
      setError('Copying was blocked by this browser. Press and hold the link to copy it.');
    }
  };

  const shareLink = async () => {
    if (!referralUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'QuikPix event enquiry',
          text: 'Tell QuikPix what you are planning using my referral link.',
          url: referralUrl,
        });
        setFeedback('Share options opened');
        return;
      } catch (shareError) {
        if (shareError instanceof DOMException && shareError.name === 'AbortError') return;
      }
    }
    await copyLink('Sharing is not available here, so the link was copied');
  };

  const createEventLink = async () => {
    const selectedEvent = safeRosteredEvents.find((event) => event.id === selectedEventId);
    if (!selectedEvent) {
      setError('Choose one of your rostered events first.');
      return;
    }

    setCreating(true);
    setError('');
    const { error: createError } = await supabase.rpc('create_my_event_referral_link', {
      p_event_shift_id: selectedEvent.id,
      p_label: selectedEvent.title ? `Referral for ${selectedEvent.title}` : 'Event referral',
      p_expires_at: null,
    });

    if (createError) {
      setError('We could not create that event link. Please confirm the event is on your roster.');
      setCreating(false);
      return;
    }

    await loadReferralData();
    setCreating(false);
    setFeedback('Event referral link created');
  };

  return (
    <div className="animate-fade-in-up space-y-6" data-testid="crew-referral-panel">
      <div className="bg-slate-900/60 border border-white/10 rounded-[32px] p-6 md:p-8 shadow-xl">
        <div className="flex items-start gap-4 mb-7 pb-6 border-b border-white/5">
          <div className="w-12 h-12 rounded-2xl bg-brand-neonBlue/10 border border-brand-neonBlue/30 text-brand-neonBlue flex items-center justify-center shrink-0">
            <Users size={23} />
          </div>
          <div className="min-w-0">
            <p className="type-label mb-1 text-brand-neonBlue">Crew referrals</p>
            <h2 className="type-heading text-2xl text-white md:text-3xl">Refer an Enquiry</h2>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              Share your personal link when someone asks about a QuikPix experience. We will handle the enquiry and keep your referral history updated here.
            </p>
          </div>
        </div>

        {error && (
          <div role="alert" className="mb-5 rounded-2xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-300">
            {error}
          </div>
        )}
        {feedback && (
          <div role="status" className="mb-5 flex items-center gap-2 rounded-2xl border border-green-500/25 bg-green-500/10 px-4 py-3 text-sm font-semibold text-green-300">
            <Check size={16} /> {feedback}
          </div>
        )}

        {loading ? (
          <div className="min-h-[260px] flex items-center justify-center text-slate-400">
            <Loader2 size={24} className="animate-spin mr-3 text-brand-neonBlue" /> Loading your referral link…
          </div>
        ) : (
          <div className="space-y-7">
            <section aria-labelledby="personal-referral-heading">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <p className="type-label text-slate-500">Always available</p>
                  <h3 id="personal-referral-heading" className="type-heading mt-1 text-lg text-white">Your personal referral link</h3>
                </div>
                <Link2 size={21} className="text-brand-neonBlue shrink-0" />
              </div>

              {!permanentLink ? (
                <div className="rounded-2xl border border-dashed border-white/10 bg-slate-950/50 p-5 text-sm text-slate-400">
                  Your permanent referral link has not been issued yet. Please contact the QuikPix team.
                </div>
              ) : (
                <div className="grid md:grid-cols-[1fr_auto] gap-5 rounded-3xl border border-brand-neonBlue/20 bg-slate-950/70 p-5">
                  <div className="min-w-0 flex flex-col justify-center">
                    <p className="text-xs text-slate-400 mb-2">Use this for general enquiries at any time.</p>
                    <a href={`${WEBSITE_BASE_URL}/r/${permanentLink.token}`} target="_blank" rel="noreferrer" className="text-sm text-brand-neonBlue hover:text-white break-all font-semibold transition-colors">
                      {`${WEBSITE_BASE_URL}/r/${permanentLink.token}`} <ExternalLink size={13} className="inline ml-1" />
                    </a>
                  </div>
                  <div className="bg-white rounded-2xl p-3 justify-self-center" aria-label="QR code for personal referral link">
                    <QRCodeSVG value={`${WEBSITE_BASE_URL}/r/${permanentLink.token}`} size={156} level="M" />
                  </div>
                </div>
              )}
            </section>

            <section aria-labelledby="event-referral-heading" className="border-t border-white/5 pt-7">
              <div className="flex items-center gap-2 mb-3">
                <QrCode size={18} className="text-brand-neonPink" />
                <h3 id="event-referral-heading" className="type-heading text-lg text-white">Create a link for your event</h3>
              </div>
              <p className="text-sm text-slate-400 mb-4">Only events that are on your roster are available.</p>
              <div className="grid sm:grid-cols-[1fr_auto] gap-3">
                <label className="sr-only" htmlFor="referral-event">Rostered event</label>
                <select
                  id="referral-event"
                  value={selectedEventId}
                  onChange={(event) => setSelectedEventId(event.target.value)}
                  className="w-full min-w-0 bg-slate-950 border border-white/10 rounded-2xl px-4 py-3.5 text-sm text-white outline-none focus:border-brand-neonBlue [color-scheme:dark]"
                >
                  <option value="">Use my permanent link</option>
                  {safeRosteredEvents.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.title || 'Rostered event'}{event.eventDate ? ` — ${new Date(event.eventDate).toLocaleDateString('en-NZ')}` : ''}
                    </option>
                  ))}
                </select>
                {selectedEventId && !selectedEventLink && (
                  <button
                    type="button"
                    onClick={createEventLink}
                    disabled={creating}
                    className="rounded-2xl bg-brand-neonPink px-5 py-3.5 text-sm font-black text-white transition-transform hover:scale-[1.02] disabled:cursor-wait disabled:opacity-60"
                  >
                    {creating ? 'Creating…' : 'Create Event Link'}
                  </button>
                )}
              </div>

              {selectedEventId && selectedEventLink && (
                <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-4 items-center rounded-2xl border border-brand-neonPink/20 bg-brand-neonPink/5 p-4">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-green-400 flex items-center gap-1.5 mb-2"><Check size={14} /> Event-specific link ready</p>
                    <a href={referralUrl} target="_blank" rel="noreferrer" className="text-xs text-brand-neonBlue hover:text-white break-all font-semibold transition-colors">
                      {referralUrl} <ExternalLink size={12} className="inline ml-1" />
                    </a>
                  </div>
                  <div className="bg-white rounded-xl p-2 justify-self-center" aria-label="QR code for event-specific referral link">
                    <QRCodeSVG value={referralUrl} size={124} level="M" />
                  </div>
                </div>
              )}
            </section>

            {referralUrl && (
              <section className="border-t border-white/5 pt-7" aria-label="Share referral link">
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" onClick={() => copyLink()} className="flex items-center justify-center gap-2 rounded-2xl border border-brand-neonBlue/30 bg-brand-neonBlue/10 px-4 py-4 text-sm font-black text-brand-neonBlue hover:bg-brand-neonBlue hover:text-slate-950 transition-colors">
                    <Copy size={18} /> Copy Link
                  </button>
                  <button type="button" onClick={shareLink} className="flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-4 text-sm font-black text-slate-950 hover:bg-brand-neonBlue transition-colors">
                    <Share2 size={18} /> Share
                  </button>
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      <div className="bg-slate-900/60 border border-white/10 rounded-[32px] p-6 md:p-8 shadow-xl">
        <div className="flex items-center gap-3 mb-5">
          <History size={20} className="text-brand-neonPink" />
          <div>
            <p className="type-label text-slate-500">Safe summary only</p>
            <h3 className="type-heading text-xl text-white">Referral history</h3>
          </div>
        </div>

        {!loading && history.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/10 bg-slate-950/40 p-6 text-center text-sm text-slate-500">
            No referred enquiries to show yet.
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((item) => (
              <article key={item.referral_id} className="rounded-2xl border border-white/5 bg-slate-950/55 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-white">{item.event_type || 'Event enquiry'}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Referred {new Date(item.referred_at).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <span className="type-label rounded-full border border-brand-neonBlue/20 bg-brand-neonBlue/10 px-3 py-1 text-brand-neonBlue">
                    {formatStatus(item.lead_status, 'In progress')}
                  </span>
                </div>
                <dl className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/5">
                  <div>
                    <dt className="type-label text-slate-500">Client benefit</dt>
                    <dd className="text-xs font-semibold text-slate-300 mt-1">{formatStatus(item.client_benefit_status, 'Pending')}</dd>
                  </div>
                  <div>
                    <dt className="type-label text-slate-500">Reward</dt>
                    <dd className="text-xs font-semibold text-slate-300 mt-1">{formatStatus(item.reward_status, 'Pending')}</dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
